/**
 * Custom API functions for reindexing Knowledge Base and Projects.
 * These are not auto-generated by orval.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { customInstance } from "~/custom-instance";
import { BASE_API_URL } from "~/utils/apiUrl";

export interface ReindexResponse {
  job_id: string;
  message: string;
  status: string;
  total_documents: number;
  case_id: string;
  tenant_id: string;
}

export interface IndexStatusResponse {
  index_status: string; // pending, indexing, indexed, completed, failed, not_found
  documents_loaded: number;
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
}

/**
 * Reindex the knowledge base for an organization.
 * This triggers sky-rag to re-process all knowledge documents with pgVector.
 */
export const reindexKnowledgeBase = (organizationId: string) => {
  return customInstance<ReindexResponse>({
    url: `${BASE_API_URL}/organizations/${organizationId}/knowledge/reindex`,
    method: "POST",
  });
};

/**
 * Get the index status for the knowledge base.
 * Knowledge base uses case_id = "knowledge-base-pages"
 */
export const getKnowledgeBaseIndexStatus = (organizationId: string) => {
  return customInstance<IndexStatusResponse>({
    url: `${BASE_API_URL}/organizations/${organizationId}/projects/knowledge-base-pages/index-status`,
    method: "GET",
  });
};

// React Query hooks

export const useReindexKnowledgeBase = () => {
  return useMutation({
    mutationFn: (organizationId: string) =>
      reindexKnowledgeBase(organizationId),
  });
};

export const useKnowledgeBaseIndexStatus = (
  organizationId: string,
  options?: { enabled?: boolean; refetchOnIndexing?: boolean }
) => {
  const query = useQuery({
    queryKey: ["knowledgeBaseIndexStatus", organizationId],
    queryFn: () => getKnowledgeBaseIndexStatus(organizationId),
    enabled: options?.enabled ?? true,
    // Refetch every 5 seconds when indexing is in progress
    refetchInterval: (query) => {
      const status = query.state.data?.index_status;
      if (options?.refetchOnIndexing !== false && status === "indexing") {
        return 5000;
      }
      return false;
    },
  });
  return query;
};
